import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Star, Loader2, MessageCircle } from "lucide-react";
import { fmtRelative } from "@/lib/format";

/**
 * Iter 253q · Compact "My Feedbacks" tile for the MyDashboard.
 * Unified list of client-submitted feedback + internal ad-hoc feedback
 * received by the current user. Newest first, capped at 5 rows.
 */
export default function MyFeedbackTile() {
  const { activeWorkspaceId } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!activeWorkspaceId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/workspaces/${activeWorkspaceId}/feedback/my?limit=5`);
      setRows(data?.rows || []);
    } catch { setRows([]); }
    finally { setLoading(false); }
  }, [activeWorkspaceId]);
  useEffect(() => { load(); }, [load]);

  return (
    <div id="feedback" className="border border-border bg-background" data-testid="my-feedback-tile">
      <div className="px-4 py-3 border-b border-border flex items-center gap-2">
        <MessageCircle className="w-4 h-4" />
        <h3 className="text-sm font-semibold">My feedback</h3>
        <span className="text-[10px] text-muted-foreground ml-1">
          {loading ? "" : `${rows.length} recent`}
        </span>
      </div>
      <div className="divide-y divide-border">
        {loading && (
          <div className="p-4 flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        )}
        {!loading && rows.length === 0 && (
          <div className="p-6 text-center text-xs text-muted-foreground italic" data-testid="my-feedback-empty">
            No feedback received yet. When a client submits a feedback request or a teammate leaves ad-hoc feedback, it will appear here.
          </div>
        )}
        {rows.map((r) => (
          <div key={`${r.source}-${r.id}`} className="px-4 py-3 hover:bg-muted/30" data-testid={`my-feedback-row-${r.id}`}>
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`text-[9px] px-1.5 py-0.5 border ${r.source === "client" ? "border-blue-500 text-blue-700" : "border-purple-500 text-purple-700"}`}>
                {r.source === "client" ? "CLIENT" : "INTERNAL"}
              </span>
              <span className="text-sm font-medium truncate">{r.title || "Feedback"}</span>
              {r.avg_rating != null && (
                <span className="ml-auto inline-flex items-center gap-0.5">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <Star key={n}
                      className={`w-3 h-3 ${n <= Math.round(r.avg_rating) ? "fill-amber-400 text-amber-400" : "text-muted-foreground"}`}
                    />
                  ))}
                  <span className="text-[10px] text-muted-foreground ml-1">{r.avg_rating}</span>
                </span>
              )}
            </div>
            <div className="text-[11px] text-muted-foreground mt-0.5">
              {r.from_name || "Someone"} · {fmtRelative(r.at)}
            </div>
            {r.comment && (
              <div className="text-xs mt-1 italic border-l-2 border-primary/30 pl-2">"{r.comment}"</div>
            )}
            {r.overall_comment && r.source === "client" && (
              <div className="text-xs mt-1 text-muted-foreground">Overall: {r.overall_comment}</div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
